import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { Sparkles, Search, AlertTriangle, Check, X } from 'lucide-react'
import { api } from '../../lib/api'
import { useAuthStore } from '../../stores/auth'
import { Card } from '../../components/ui/Card'
import { Button } from '../../components/ui/Button'

/**
 * Asistentul de corespondență — ciorne de răspuns.
 *
 * Două lucruri de știut, ambele structurale:
 *
 * CRM-ul nu trimite emailuri. Ciorna aprobată ajunge în folderul Drafts al căsuței, iar
 * trimiterea rămâne un gest omenesc, din clientul de email.
 *
 * Ciorna se compară cu ce a primit modelul. Semnalele de sub text arată exact ce nu s-a
 * verificat — o cifră care nu apare nicăieri în fir, o limbă greșită, o lungime suspectă.
 */

interface Validare {
  cod: string
  trecut: boolean
  detaliu: string | null
}

interface Ciorna {
  id: number
  profil: string
  generatedText: string
  finalText: string | null
  status: string
  validari: Validare[] | null
  generatedAt: string
  writtenAt: string | null
  eroare: string | null
  /** `raspuns` pleacă înapoi la expeditor; `compunere` e un mesaj nou, către altcineva. */
  fel: string
  catre: string[]
  subiect: string | null
  message: {
    id: number
    fromAddr: string | null
    subject: string | null
    receivedAt: string
    bodyText: string
  }
}

interface MesajCandidat {
  id: number
  fromAddr: string | null
  subject: string | null
  receivedAt: string
}

const cand = (s: string | null) =>
  s ? new Date(s).toLocaleString('ro-RO', { dateStyle: 'short', timeStyle: 'short' }) : '—'

/**
 * Mesajul de eroare al API-ului.
 *
 * Forma lui e `{ error: { code, message } }`. Pagina citea `data.message`, care nu exista —
 * asa ca ORICE eroare de aici arata la fel, „Generarea a esuat", si nimeni nu putea afla de ce.
 * Prima data cand a contat: o instructiune prea lunga, respinsa de validare, fara niciun indiciu.
 */
function mesajApi(e: unknown, implicit: string): string {
  const err = e as {
    response?: { data?: { error?: { message?: string; fields?: Record<string, string[]> } } }
  }
  const eroare = err.response?.data?.error
  // La o eroare de validare, `message` e generic („Invalid request payload"), iar motivul real
  // sta pe camp. Fara asta, mesajul ar fi ramas la fel de neajutator ca inainte.
  const peCamp = Object.values(eroare?.fields ?? {})[0]?.[0]
  return peCamp ?? eroare?.message ?? implicit
}

export function CorespondentaPage() {
  const qc = useQueryClient()
  const permissions = useAuthStore((s) => s.permissions)
  const poateScrie = permissions.includes('corespondenta:write')
  const [status, setStatus] = useState<'generata' | 'scrisa' | 'respinsa' | 'toate'>('generata')
  const [cauta, setCauta] = useState('')
  const [deschisa, setDeschisa] = useState<Ciorna | null>(null)

  const { data: ciorne } = useQuery<{ ciorne: Ciorna[] }>({
    queryKey: ['corespondenta', 'ciorne', status],
    queryFn: async () => (await api.get('/corespondenta/ciorne', { params: { status } })).data,
  })

  const { data: candidate } = useQuery<{ mesaje: MesajCandidat[] }>({
    queryKey: ['corespondenta', 'candidate', cauta],
    queryFn: async () =>
      (await api.get('/corespondenta/candidate', { params: cauta ? { q: cauta } : {} })).data,
  })

  const genereaza = useMutation({
    mutationFn: async (inboxMessageId: number) =>
      (
        await api.post(
          '/corespondenta/ciorne',
          { inboxMessageId },
          // Pragul obisnuit de 15 secunde e prea scurt aici: modelul scrie un email in 10-20 de
          // secunde. La 17 secunde browserul renunta si arata „Generarea a esuat", desi ciorna
          // se salveaza — utilizatorul o vede aparuta in lista imediat dupa mesajul de eroare.
          { timeout: 120_000 },
        )
      ).data,
    onSuccess: () => {
      toast.success('Ciornă generată. Citește-o înainte s-o aprobi.')
      void qc.invalidateQueries({ queryKey: ['corespondenta'] })
    },
    onError: (e: unknown) => toast.error(mesajApi(e, 'Generarea a eșuat.')),
  })

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-xl font-semibold tracking-tight">Corespondență — asistent AI</h1>
        <p className="mt-0.5 text-sm text-b4l-text-muted">
          Generează ciorne de răspuns pe care le citești și le corectezi. Ciorna aprobată se scrie
          în folderul <strong>Drafts</strong> al căsuței; trimiterea o faci tu, din clientul de
          email. CRM-ul nu are dreptul tehnic să expedieze.
        </p>
      </div>

      <div className="grid min-w-0 gap-4 lg:grid-cols-[1fr_1.2fr] [&>*]:min-w-0">
        <Card title="Mesaje fără ciornă" description="Alege un mesaj și cere o ciornă de răspuns">
          <div className="relative mb-2">
            <Search
              size={15}
              className="pointer-events-none absolute left-2 top-1/2 -translate-y-1/2 text-b4l-text-muted"
            />
            <input
              className="w-full rounded border border-b4l-border py-1.5 pl-7 pr-2 text-sm"
              placeholder="Caută după subiect sau expeditor"
              value={cauta}
              onChange={(e) => setCauta(e.target.value)}
            />
          </div>
          <div className="max-h-[26rem] divide-y divide-b4l-border/60 overflow-y-auto">
            {(candidate?.mesaje ?? []).length === 0 ? (
              <p className="py-8 text-center text-sm text-b4l-text-muted">Niciun mesaj.</p>
            ) : (
              (candidate?.mesaje ?? []).map((m) => (
                <div key={m.id} className="flex min-w-0 items-start gap-2 py-2 text-sm">
                  <span className="min-w-0 flex-1">
                    <span className="block truncate">{m.subject ?? '(fără subiect)'}</span>
                    <span className="block truncate text-xs text-b4l-text-muted">
                      {m.fromAddr} · {cand(m.receivedAt)}
                    </span>
                  </span>
                  {poateScrie && (
                    <Button
                      variant="ghost"
                      loading={genereaza.isPending}
                      onClick={() => genereaza.mutate(m.id)}
                    >
                      <Sparkles size={14} /> Ciornă
                    </Button>
                  )}
                </div>
              ))
            )}
          </div>
        </Card>

        <Card title="Ciorne">
          <div className="mb-2 flex gap-1">
            {(
              [
                ['generata', 'De revizuit'],
                ['scrisa', 'Scrise'],
                ['respinsa', 'Respinse'],
                ['toate', 'Toate'],
              ] as const
            ).map(([k, eticheta]) => (
              <button
                key={k}
                onClick={() => setStatus(k)}
                className={`rounded px-2 py-1 text-xs ${
                  status === k ? 'bg-b4l-blue text-white' : 'text-b4l-text-muted hover:bg-b4l-bg'
                }`}
              >
                {eticheta}
              </button>
            ))}
          </div>
          <div className="max-h-[26rem] divide-y divide-b4l-border/60 overflow-y-auto">
            {(ciorne?.ciorne ?? []).length === 0 ? (
              <p className="py-8 text-center text-sm text-b4l-text-muted">Nicio ciornă.</p>
            ) : (
              (ciorne?.ciorne ?? []).map((c) => {
                const semnale = (c.validari ?? []).filter((v) => !v.trecut)
                return (
                  <button
                    key={c.id}
                    onClick={() => setDeschisa(c)}
                    className="flex w-full items-start gap-2 py-2 text-left text-sm hover:bg-b4l-bg"
                  >
                    <span className="min-w-0 flex-1">
                      <span className="block truncate">
                        {c.message.subject ?? '(fără subiect)'}
                      </span>
                      <span className="block truncate text-xs text-b4l-text-muted">
                        {c.message.fromAddr} · {cand(c.generatedAt)}
                      </span>
                    </span>
                    {semnale.length > 0 && (
                      <span className="flex shrink-0 items-center gap-1 text-xs text-amber-700">
                        <AlertTriangle size={13} /> {semnale.length}
                      </span>
                    )}
                    {c.writtenAt && <Check size={14} className="shrink-0 text-emerald-600" />}
                  </button>
                )
              })
            )}
          </div>
        </Card>
      </div>

      {deschisa && (
        <Revizuire ciorna={deschisa} poateScrie={poateScrie} onClose={() => setDeschisa(null)} />
      )}
    </div>
  )
}

/** Ecranul de revizuire: firul original, ciorna editabilă, semnalele de validare. */
function Revizuire({
  ciorna,
  poateScrie,
  onClose,
}: {
  ciorna: Ciorna
  poateScrie: boolean
  onClose: () => void
}) {
  const qc = useQueryClient()
  const [text, setText] = useState(ciorna.finalText ?? ciorna.generatedText)
  const compunere = ciorna.fel === 'compunere'
  const [catre, setCatre] = useState(ciorna.catre.join(', '))
  const [subiect, setSubiect] = useState(ciorna.subiect ?? ciorna.message.subject ?? '')
  const semnale = (ciorna.validari ?? []).filter((v) => !v.trecut)
  const [indicatii, setIndicatii] = useState('')

  /**
   * Regenerarea cu indicații.
   *
   * Multe mesaje interne sunt note de două cuvinte — spun cui, nu ce. Fără câmpul ăsta,
   * răspunsul onest al asistentului („nu conține textul de transmis") era un capăt de drum,
   * iar omul își scria mailul în altă parte.
   */
  const regenereaza = useMutation({
    mutationFn: async () =>
      (
        await api.post(
          '/corespondenta/ciorne',
          { inboxMessageId: ciorna.message.id, instructiune: indicatii.trim() },
          { timeout: 120_000 },
        )
      ).data,
    onSuccess: () => {
      toast.success('Ciornă refăcută cu indicațiile tale.')
      setIndicatii('')
      void qc.invalidateQueries({ queryKey: ['corespondenta'] })
      onClose()
    },
    onError: (e: unknown) => toast.error(mesajApi(e, 'Regenerarea a eșuat.')),
  })

  const listaCatre = catre
    .split(/[,;]/)
    .map((a) => a.trim())
    .filter(Boolean)

  const aproba = useMutation({
    mutationFn: async () =>
      (
        await api.post(`/corespondenta/ciorne/${ciorna.id}/aproba`, {
          text,
          ...(compunere && { catre: listaCatre, subiect }),
        })
      ).data,
    onSuccess: (r: { ok: boolean; mesaj: string }) => {
      if (r.ok) toast.success(r.mesaj)
      else toast.error(r.mesaj)
      void qc.invalidateQueries({ queryKey: ['corespondenta'] })
      if (r.ok) onClose()
    },
    onError: (e: unknown) => toast.error(mesajApi(e, 'Scrierea a eșuat.')),
  })

  const respinge = useMutation({
    mutationFn: async (motiv: string) =>
      (await api.post(`/corespondenta/ciorne/${ciorna.id}/respinge`, { motiv })).data,
    onSuccess: () => {
      toast.success('Ciornă respinsă.')
      void qc.invalidateQueries({ queryKey: ['corespondenta'] })
      onClose()
    },
  })

  return (
    <Card
      title={ciorna.message.subject ?? '(fără subiect)'}
      description={`${ciorna.message.fromAddr} · primit ${cand(ciorna.message.receivedAt)}`}
      actions={
        <Button variant="ghost" onClick={onClose}>
          <X size={16} />
        </Button>
      }
    >
      {/* `min-w-0` pe fiecare copil de grila: implicit au `min-width: auto`, iar un singur
          cuvant lung din email — un link, un identificator — umfla coloana si impinge toata
          pagina in lateral. Pe telefon rezultatul era un ecran gol, cu continutul iesit din
          cadru. `break-words` rupe si cuvintele care n-au unde altundeva sa se rupa. */}
      <div className="grid gap-4 lg:grid-cols-2">
        <div className="min-w-0">
          <div className="mb-1 text-xs font-medium uppercase tracking-wide text-b4l-text-muted">
            Mesajul original
          </div>
          <pre className="max-h-96 overflow-auto whitespace-pre-wrap break-words rounded border border-b4l-border bg-b4l-bg p-3 text-xs">
            {ciorna.message.bodyText}
          </pre>
        </div>

        <div className="min-w-0">
          <div className="mb-1 flex flex-wrap items-center gap-2 text-xs font-medium uppercase tracking-wide text-b4l-text-muted">
            Ciorna {ciorna.writtenAt && '(deja scrisă în căsuță)'}
            {compunere && (
              <span
                className="rounded bg-b4l-blue-light px-1.5 py-0.5 text-[10px] normal-case tracking-normal text-b4l-blue-dark"
                title="Mesajul original e o instrucțiune, nu o întrebare: ciorna nu se întoarce la expeditor"
              >
                mesaj nou
              </span>
            )}
          </div>

          {/* Destinatarii se arata doar cand ciorna chiar pleaca altundeva. La un raspuns,
              destinatarul e expeditorul firului si un camp editabil ar sugera altceva. */}
          {compunere && (
            <div className="mb-2 space-y-2">
              <label className="block">
                <span className="mb-0.5 block text-xs text-b4l-text-muted">
                  Către{' '}
                  {ciorna.catre.length === 0 && (
                    <span className="text-b4l-amber-dark">
                      — completează tu, n-am adrese sigure
                    </span>
                  )}
                </span>
                <input
                  className="w-full rounded border border-b4l-border px-2 py-1.5 text-sm"
                  placeholder="adrese separate prin virgulă"
                  value={catre}
                  disabled={!poateScrie || !!ciorna.writtenAt}
                  onChange={(e) => setCatre(e.target.value)}
                />
              </label>
              <label className="block">
                <span className="mb-0.5 block text-xs text-b4l-text-muted">Subiect</span>
                <input
                  className="w-full rounded border border-b4l-border px-2 py-1.5 text-sm"
                  value={subiect}
                  disabled={!poateScrie || !!ciorna.writtenAt}
                  onChange={(e) => setSubiect(e.target.value)}
                />
              </label>
            </div>
          )}

          <textarea
            className={`w-full rounded border border-b4l-border p-3 text-sm ${compunere ? 'h-72' : 'h-96'}`}
            value={text}
            disabled={!poateScrie || !!ciorna.writtenAt}
            onChange={(e) => setText(e.target.value)}
          />
        </div>
      </div>

      {semnale.length > 0 && (
        <div className="mt-3 rounded border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
          <div className="mb-1 flex items-center gap-1.5 font-medium">
            <AlertTriangle size={15} /> De verificat înainte de aprobare
          </div>
          <ul className="list-inside list-disc space-y-0.5 text-xs">
            {semnale.map((v) => (
              <li key={v.cod}>
                <strong>{v.cod}</strong> — {v.detaliu}
              </li>
            ))}
          </ul>
        </div>
      )}

      {ciorna.eroare && (
        <p className="mt-2 text-xs text-b4l-red">Ultima încercare a eșuat: {ciorna.eroare}</p>
      )}

      {poateScrie && !ciorna.writtenAt && (
        <div className="mt-3 rounded-lg border border-b4l-border p-3">
          <label className="block">
            <span className="text-xs font-medium text-b4l-text">
              Spune-i ce să scrie și refă ciorna
            </span>
            <textarea
              value={indicatii}
              onChange={(e) => setIndicatii(e.target.value)}
              rows={2}
              placeholder="Ex.: e un email de introducere în CRM — ce pot face acolo și pe cine întreabă dacă se blochează. Poți lipi și un document întreg."
              className="mt-1 w-full rounded border border-b4l-border p-2 text-sm"
            />
          </label>
          <p
            className={`mt-1 text-xs ${
              indicatii.length > 20000 ? 'text-b4l-red' : 'text-b4l-text-muted'
            }`}
          >
            {indicatii.length.toLocaleString('ro-RO')} / 20.000 caractere
          </p>
          <div className="mt-2 flex justify-end">
            <Button
              variant="secondary"
              loading={regenereaza.isPending}
              disabled={indicatii.trim().length < 5 || indicatii.length > 20000}
              onClick={() => regenereaza.mutate()}
            >
              Refă ciorna
            </Button>
          </div>
        </div>
      )}

      {poateScrie && !ciorna.writtenAt && (
        <div className="mt-3 flex justify-end gap-2">
          <Button
            variant="secondary"
            loading={respinge.isPending}
            onClick={() => {
              const motiv = window.prompt('De ce o respingi? (scurt, ajută la reglaj)') ?? ''
              respinge.mutate(motiv)
            }}
          >
            Respinge
          </Button>
          <Button loading={aproba.isPending} onClick={() => aproba.mutate()}>
            <Check size={16} /> Scrie în Drafts
          </Button>
        </div>
      )}
    </Card>
  )
}
